import React, { useState, useMemo, useRef, useEffect } from 'react';
import { DownloadOutlined, MenuOutlined } from '@ant-design/icons';
import { Trash2 } from 'lucide-react';
import { Dropdown, Menu } from 'antd';
import { Button } from './ui/button';
import { GraphParameterSelector, GraphParameter } from './ui/GraphParameterSelector';
import { convertWeldDataValue, getParameterUnitSuffix, getUnitFromSettings } from '../utils/unitUtils';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ChartOptions,
  ChartData,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend
);

interface ViewGraphProps {
  detailData: any[];
  projectNumber?: string;
  projectName?: string;
}

interface GraphConfig {
  id: string;
  parameter: string;
  enabled: boolean;
}

const ViewGraph: React.FC<ViewGraphProps> = ({
  detailData,
  projectNumber = '',
  projectName = '',
}) => {
  const [graphConfigs, setGraphConfigs] = useState<GraphConfig[]>([
    { id: '1', parameter: 'travel_speed', enabled: true },
    { id: '2', parameter: 'volts', enabled: true },
  ]);

  const processedDataRef = useRef<{
    categories: number[];
    graphData: Record<string, number[]>;
  }>({
    categories: [],
    graphData: {},
  });

  // DOM ready check to prevent chart rendering issues
  const [isDomReady, setIsDomReady] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsDomReady(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // Get dynamic unit labels for parameters
  const getUnitLabel = (parameter: string): string => {
    return getParameterUnitSuffix(parameter);
  };

  // Available parameters with their display names and colors
  const availableParameters: GraphParameter[] = [
    { value: 'travel_speed', label: `Travel Speed (${getUnitLabel('travel_speed')})`, color: '#EB1C37' },
    { value: 'volts', label: `Volts (${getUnitLabel('volts')})`, color: '#FFA500' },
    { value: 'current', label: `Current (${getUnitLabel('current')})`, color: '#ee82ee' },
    { value: 'wire_speed', label: `Wire Speed (${getUnitLabel('wire_speed')})`, color: '#800080' },
    { value: 'oscillation_width', label: `Oscillate Width (${getUnitLabel('oscillation_width')})`, color: '#A52A2A' },
    { value: 'target', label: `Target (${getUnitLabel('target')})`, color: '#808080' },
    { value: 'horizontal_bias', label: `Horizontal Bias (${getUnitLabel('horizontal_bias')})`, color: '#00FFFF' },
    { value: 'frequency', label: `Frequency (${getUnitLabel('frequency')})`, color: '#FFC0CB' },
    { value: 'total_wire_consumed', label: `Total Wire Consumed (${getUnitLabel('total_wire_consumed')})`, color: '#FFFF00' },
    { value: 'true_energy', label: `True Energy (${getUnitLabel('true_energy')})`, color: '#0000FF' },
    { value: 'heat', label: `Heat (${getUnitLabel('heat')})`, color: '#008000' },
    { value: 'arc_impedance', label: `Arc Impedance (${getUnitLabel('arc_impedance')})`, color: '#FF6347' },
  ];

  // Process data for charts
  const processedData = useMemo(() => {
    if (!detailData || detailData.length === 0) {
      return { categories: [], graphData: {} };
    }

    try {
      // Use tilt values for x-axis categories, fallback to data point index if tilt not available
      const categories = detailData.map((item, index) => {
        // Check for tilt value in various possible field names
        const tiltValue = item.tilt || item.tilt_range || item.tiltRange || item.range || (index + 1);
        return typeof tiltValue === 'number' ? tiltValue : (index + 1);
      });
      
      const graphData: Record<string, number[]> = {};

      // Process each parameter
      availableParameters.forEach(param => {
        const data = detailData.map(item => {
          const value = item[param.value];
          const numericValue = typeof value === 'number' ? value : parseFloat(value) || 0;
          // Convert parameter names to match convertWeldDataValue expectations
          const paramName = param.value === 'oscillation_width' ? 'oscillationWidth' : 
                           param.value === 'travel_speed' ? 'travelSpeed' :
                           param.value === 'wire_speed' ? 'wireSpeed' :
                           param.value === 'true_energy' ? 'trueEnergy' :
                           param.value === 'total_wire_consumed' ? 'totalWireConsumed' :
                           param.value;
          return convertWeldDataValue(numericValue, paramName);
        });
        
        // Filter out invalid data points
        const validData = data.filter(value => value !== null && value !== undefined && !isNaN(value));
        
        if (validData.length > 0) {
          graphData[param.value] = validData;
        }
      });

      return { categories, graphData };
    } catch (error) {
      return { categories: [], graphData: {} };
    }
  }, [detailData]);

  // Handle parameter selection
  const handleParameterChange = (selected: string[]) => {
    
    // Get currently displayed parameters
    const currentParameters = graphConfigs.map(config => config.parameter);
    
    // Find parameters to add (in selected but not in current)
    const parametersToAdd = selected.filter(param => !currentParameters.includes(param));
    
    // Find parameters to remove (in current but not in selected)
    const parametersToRemove = currentParameters.filter(param => !selected.includes(param));
    
    // Add new graphs
    parametersToAdd.forEach(parameter => {
      const newId = (graphConfigs.length + 1).toString();
      const newConfig: GraphConfig = {
        id: newId,
        parameter: parameter,
        enabled: true
      };
      setGraphConfigs(prev => [...prev, newConfig]);
    });
    
    // Remove graphs
    if (parametersToRemove.length > 0) {
      setGraphConfigs(prev => prev.filter(config => !parametersToRemove.includes(config.parameter)));
    }
  };

  // Remove graph
  const removeGraph = (id: string) => {
    setGraphConfigs(prev => prev.filter(config => config.id !== id));
  };

  // Toggle graph visibility
  const toggleGraph = (id: string) => {
    setGraphConfigs(prev => prev.map(config => 
      config.id === id 
        ? { ...config, enabled: !config.enabled }
        : config
    ));
  };

  // Download CSV for a specific graph
  const downloadCSV = (parameter: string) => {
    const { categories, graphData } = processedDataRef.current;
    const data = graphData[parameter];
    const paramLabel = availableParameters.find(p => p.value === parameter)?.label || parameter;

    if (!categories.length || !data) {
      return;
    }

    const headers = ['Tilt', paramLabel];
    const rows = categories.map((category, index) => [category, data[index]]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `${projectNumber}_${projectName}_${paramLabel}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Get chart data for a specific parameter
  const getChartData = (parameter: string): ChartData<'line'> => {
    const data = processedData.graphData[parameter] || [];
    const paramLabel = availableParameters.find(p => p.value === parameter)?.label || parameter;
    const paramColor = availableParameters.find(p => p.value === parameter)?.color || '#000000';

    return {
      labels: processedData.categories,
      datasets: [{
        label: paramLabel,
        data: data,
        borderColor: paramColor,
        backgroundColor: paramColor + '20',
        borderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 6,
        pointBackgroundColor: paramColor,
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        tension: 0.1,
        fill: false,
      }],
    };
  };

  // Chart options
  const getChartOptions = (parameter: string): ChartOptions<'line'> => {
    const paramLabel = availableParameters.find(p => p.value === parameter)?.label || parameter;
    
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: {},
      plugins: {
        legend: {
          display: true,
          position: 'top' as const,
          labels: {
            color: '#374151',
            font: {
              size: 12,
            },
            usePointStyle: true,
            pointStyle: 'circle',
          },
        },
        title: {
          display: true,
          text: paramLabel,
          color: '#374151',
          font: {
            size: 14,
            weight: 'bold',
          },
        },
        tooltip: {
          enabled: true,
          mode: 'index' as const,
          intersect: false,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#ffffff',
          bodyColor: '#ffffff',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          cornerRadius: 8,
          displayColors: true,
        },
      },
      interaction: {
        mode: 'nearest' as const,
        axis: 'x' as const,
        intersect: false,
      },
      elements: {
        point: {
          hoverRadius: 6,
        },
      },
      scales: {
        x: {
          display: true,
          title: {
            display: true,
            text: 'Tilt',
            color: '#374151',
          },
          grid: {
            display: true,
            color: '#e5e7eb',
          },
          ticks: {
            color: '#374151',
          },
        },
        y: {
          display: true,
          title: {
            display: true,
            text: paramLabel,
            color: '#374151',
          },
          grid: {
            display: true,
            color: '#e5e7eb',
          },
          ticks: {
            color: '#374151',
          },
        },
      },
    };
  };

  // Get chart menu
  const getChartMenu = (parameter: string) => (
    <Menu>
      <Menu.Item 
        key="download" 
        icon={<DownloadOutlined />}
        onClick={() => downloadCSV(parameter)}
      >
        Download CSV
      </Menu.Item>
    </Menu>
  );

  if (!detailData || detailData.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
        <div className="text-center">
          <div className="text-gray-500 text-lg mb-2">No data available</div>
          <div className="text-gray-400 text-sm">Please select a pass to view graph data</div>
        </div>
      </div>
    );
  }

  // Filter enabled graphs that have data
  const enabledGraphsWithData = graphConfigs
    .filter(config => config.enabled)
    .map(config => {
      const param = availableParameters.find(p => p.value === config.parameter);
      const data = processedData.graphData[config.parameter];
      return {
        ...config,
        param,
        hasData: data && data.some(value => value !== 0)
      };
    })
    .filter(graph => graph.param && graph.hasData);

  return (
    <div className="view-graph">
      {/* Parameter Selection */}
      <div className="mb-6">
        <GraphParameterSelector
          parameters={availableParameters}
          selected={graphConfigs.map(config => config.parameter)}
          onChange={handleParameterChange}
          title="Select Graph Parameters to Display"
          availableData={Object.fromEntries(
            availableParameters.map(param => [
              param.value, 
              processedData.graphData[param.value] && processedData.graphData[param.value].some(value => value !== 0)
            ])
          )}
        />
      </div>

      {/* Graphs Grid - 2 per row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {enabledGraphsWithData.map((graph) => (
          <div
            key={graph.id}
            className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
          >
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: graph.param?.color }}
                  />
                  <span className="font-medium text-gray-800">
                    {graph.param?.label}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Dropdown
                    overlay={getChartMenu(graph.parameter)}
                    trigger={['click']}
                    placement="bottomRight"
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <MenuOutlined />
                    </Button>
                  </Dropdown>
                  
                  {graphConfigs.length > 1 && (
                    <Button
                      onClick={() => removeGraph(graph.id)}
                      variant="destructive"
                      size="sm"
                      className="p-2"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
            
            <div className="p-4">
              <div className="h-64">
                {!isDomReady ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-gray-500">Preparing charts...</div>
                  </div>
                ) : (
                  <Line data={getChartData(graph.parameter)} options={getChartOptions(graph.parameter)} />
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {enabledGraphsWithData.length === 0 && (
        <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
          <div className="text-center">
            <div className="text-gray-500 text-lg mb-2">No graph data available</div>
            <div className="text-gray-400 text-sm">
              No parameters have valid data to display
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewGraph; 